import { RuleData, RuleType, TdaRulesData } from "../types/pdf-generator";

interface MarkdownOptions {
  includeProcedures?: boolean;
  includeExamples?: boolean;
  includeGlossary?: boolean;
  examples?: { inline?: boolean };
  version?: string;
  mainTitle?: string;
}

function isGlossaryRule(rule: RuleData): boolean {
  return String(rule.id).startsWith("GL");
}

function glossaryEnabled(options: MarkdownOptions): boolean {
  return options.includeGlossary !== false && Number(options.version) >= 2026;
}

function renderChildren(node: Node): string {
  let out = "";
  node.childNodes.forEach((child) => {
    out += renderNode(child);
  });
  return out;
}

function markdownAlt(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\]/g, "\\]");
}

function markdownDestination(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\)/g, "\\)");
}

function renderMarkdownImage(el: HTMLElement): string {
  const src = el.getAttribute("src") || "";
  if (!src) return "";
  const alt = el.getAttribute("alt") || "";
  return `![${markdownAlt(alt)}](${markdownDestination(src)})`;
}

/** Anchor whose only content is an image (optional breaks and whitespace). */
function linkedImage(el: HTMLElement): HTMLElement | null {
  let image: HTMLElement | null = null;
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      if ((child.textContent || "").trim()) return null;
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const childEl = child as HTMLElement;
    if (childEl.tagName === "BR") continue;
    if (childEl.tagName === "IMG") {
      if (image) return null;
      image = childEl;
      continue;
    }
    return null;
  }
  return image;
}

function nextElementIsBr(el: HTMLElement): boolean {
  let sibling = el.nextSibling;
  while (
    sibling &&
    sibling.nodeType === Node.TEXT_NODE &&
    !(sibling.textContent || "").trim()
  ) {
    sibling = sibling.nextSibling;
  }
  return (
    sibling?.nodeType === Node.ELEMENT_NODE &&
    (sibling as HTMLElement).tagName === "BR"
  );
}

function renderNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent || "").replace(/[ \t\r\n]+/g, " ");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const el = node as HTMLElement;
  const tag = el.tagName;

  if (tag === "BR") return "\n";
  if (tag === "ALT_SPAN") return "";
  if (tag === "B" || tag === "STRONG") {
    const inner = renderChildren(el).trim();
    return inner ? `**${inner}**` : "";
  }
  if (tag === "I" || tag === "EM") {
    const inner = renderChildren(el).trim();
    return inner ? `*${inner}*` : "";
  }
  if (tag === "CODE") {
    const inner = renderChildren(el).replace(/`/g, "\\`").trim();
    return inner ? `\`${inner}\`` : "";
  }
  if (tag === "IMG") {
    const image = renderMarkdownImage(el);
    return image ? `${image}\n` : "";
  }
  if (tag === "A") {
    const image = linkedImage(el);
    if (image) {
      const imageMd = renderMarkdownImage(image);
      if (!imageMd) return "";
      const href = el.getAttribute("href") || "";
      const lineBreak = nextElementIsBr(el) ? "" : "\n";
      return href
        ? `[${imageMd}](${markdownDestination(href)})${lineBreak}`
        : `${imageMd}\n`;
    }
    const href = el.getAttribute("href") || "";
    const inner = renderChildren(el).trim();
    if (!inner) return "";
    return href ? `[${inner}](${href})` : inner;
  }
  if (tag === "UL") return `\n${renderList(el, false)}\n`;
  if (tag === "OL") return `\n${renderList(el, true)}\n`;
  return renderChildren(el);
}

function renderList(list: HTMLElement, ordered: boolean, depth = 0): string {
  const items: string[] = [];
  let index = 1;

  list.childNodes.forEach((child) => {
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const el = child as HTMLElement;
    if (el.tagName !== "LI") return;

    const indent = "  ".repeat(depth);
    const marker = ordered ? `${index}. ` : "- ";
    index += 1;

    let inline = "";
    const nested: string[] = [];
    el.childNodes.forEach((sub) => {
      if (sub.nodeType === Node.ELEMENT_NODE) {
        const subEl = sub as HTMLElement;
        if (subEl.tagName === "UL" || subEl.tagName === "OL") {
          nested.push(renderList(subEl, subEl.tagName === "OL", depth + 1));
          return;
        }
      }
      inline += renderNode(sub);
    });

    inline = inline
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{2,}/g, "\n")
      .trim();
    items.push(`${indent}${marker}${inline}`);
    nested.forEach((block) => items.push(block));
  });

  return items.join("\n");
}

function htmlToMarkdown(html: string): string {
  if (!html || !html.trim() || typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(
    `<div id="md-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("md-root");
  if (!root) return "";
  return renderChildren(root)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function formatBlock(rule: RuleData): string {
  const id = String(rule.id);
  const title = htmlToMarkdown(rule.title || "");
  const body = htmlToMarkdown(rule.text || "");
  const glossary = id.startsWith("GL");

  let heading = title;
  if (rule.type === RuleType.CHAPTER) {
    heading = `# ${title}`;
  } else if (
    rule.type === RuleType.HEADING ||
    (glossary && rule.type === RuleType.INFO)
  ) {
    heading = `## ${title}`;
  } else if (rule.type === RuleType.IA) {
    heading = `>#### ${id}: ${title}`;
  } else {
    heading = `### ${id}: ${title}`;
  }

  if (rule.type === RuleType.IA) {
    const quoted = body
      .split("\n")
      .map((line) => (line.length > 0 ? `> ${line}` : ">"))
      .join("\n");
    return quoted ? `${heading}\n>\n${quoted}` : heading;
  }

  return body ? `${heading}\n\n${body}` : heading;
}

/**
 * Appendix example title, same rule as the PDF generator:
 * localized rule word + number taken from the id after the "IA" prefix.
 */
function formatAppendixExample(rule: RuleData, ruleWord: string): string {
  const rawTitle = rule.title || "";
  const plainTitle = rawTitle.replace(/<\/?code[^>]*>/gi, "").trim();
  const ruleNumber = String(rule.id).substring(2);
  let title = htmlToMarkdown(rawTitle);
  const ruleId = String(rule.id);
  if (!plainTitle.startsWith(ruleWord)) {
    title = `${ruleWord} ${ruleNumber} : ${title}`;
  }
  const body = htmlToMarkdown(rule.text || "");
  const heading = `### ${ruleId}: ${title}`;
  return body ? `${heading}\n\n${body}` : heading;
}

/**
 * Build a Markdown document from TDA rules JSON.
 * Section order matches the PDF: rules, examples appendix, then glossary.
 */
export function buildTdaMarkdown(
  rulesData: TdaRulesData,
  options: MarkdownOptions = {},
  lang: string = "en",
): string {
  const parts: string[] = [];
  const title = rulesData.meta?.pdf_title || options.mainTitle || "TDA Rules";
  const version = rulesData.meta?.version || options.version || "2026";

  if (rulesData.meta?.author) parts.push("AUTHOR: " + rulesData.meta.author);
  if (version) parts.push("VERSION: " + version);
  if (lang) parts.push("LANGUAGE: " + lang);

  parts.push(`# ${title}`);

  const examples: RuleData[] = [];
  const glossary: RuleData[] = [];
  const includeExamples = options.includeExamples !== false;
  const inlineExamples = options.examples?.inline === true;
  const includeProcedures = options.includeProcedures !== false;
  const includeGlossary = glossaryEnabled(options);

  for (const rule of rulesData.rules) {
    const id = String(rule.id);
    if (id === "PDF" || id === "H99") continue;

    if (isGlossaryRule(rule)) {
      if (includeGlossary) glossary.push(rule);
      continue;
    }

    if (rule.type === RuleType.IA) {
      if (!includeExamples) continue;
      if (inlineExamples) parts.push(formatBlock(rule));
      else examples.push(rule);
      continue;
    }

    if (
      !includeProcedures &&
      (rule.type === RuleType.RP ||
        (rule.type === RuleType.CHAPTER && id.startsWith("RP")))
    ) {
      continue;
    }

    parts.push(formatBlock(rule));
  }

  if (examples.length > 0) {
    const ruleWord = rulesData.meta?.rule_title || "Rule";
    parts.push(`## ${rulesData.meta?.annexe || "Annexe"}`);
    examples.forEach((example) =>
      parts.push(formatAppendixExample(example, ruleWord)),
    );
  }

  glossary.forEach((rule) => parts.push(formatBlock(rule)));

  if (rulesData.meta?.footer?.length) {
    parts.push("---");
    parts.push(rulesData.meta.footer.join("\n\n"));
  }

  return `${parts.filter((part) => part && part.trim()).join("\n\n")}\n`;
}

export function downloadMarkdown(markdown: string, filename: string): void {
  const blob = new Blob([markdown], {
    type: "text/markdown;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
